import { motion } from "framer-motion";
import { MapPin, Car, Clock } from "lucide-react";
import { getStatus } from "@/physio-demo/hours";

const EASE = [0.16, 1, 0.3, 1];

const HOURS_ROWS = [
    { id: "hours-monday-thursday", days: "Monday – Thursday", time: "8:00 – 6:30", dayNums: [1, 2, 3, 4] },
    { id: "hours-friday", days: "Friday", time: "8:00 – 4:00", dayNums: [5] },
    { id: "hours-saturday", days: "Saturday", time: "9:00 – 1:00", dayNums: [6] },
    { id: "hours-sunday", days: "Sunday", time: "Closed", dayNums: [0] },
];

export const HoursLocation = () => {
    const status = getStatus();
    const today = new Date().getDay();
    return (
        <section id="hours" data-testid="hours-section" className="border-b border-line">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 py-24 sm:px-8 sm:py-32 lg:grid-cols-2">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.9, ease: EASE }}
                >
                    <p
                        className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-mutedink"
                        data-testid="location-label"
                    >
                        Find us
                    </p>
                    <h2
                        className="font-syne text-4xl font-bold leading-[0.95] tracking-tighter sm:text-5xl lg:text-6xl"
                        data-testid="location-address"
                    >
                        412 Elm
                        <br />
                        Street<span className="text-ochre">.</span>
                    </h2>
                    <p className="mt-6 max-w-md text-base leading-relaxed text-mutedink">
                        Physio on Elm, next to King. An independent neighbourhood clinic — walk in
                        off the street, park out back, and be seen without a gatekeeper.
                    </p>
                    <ul className="mt-10 flex flex-col gap-4">
                        <li className="flex items-center gap-4 border-t border-line pt-4" data-testid="location-transit">
                            <MapPin size={16} className="shrink-0 text-ochre" />
                            <span className="text-sm text-mutedink">
                                Corner of Elm &amp; King — steps from the King streetcar stop
                            </span>
                        </li>
                        <li className="flex items-center gap-4 border-t border-line pt-4" data-testid="location-parking">
                            <Car size={16} className="shrink-0 text-ochre" />
                            <span className="text-sm text-mutedink">
                                Free customer parking on-site, behind the building
                            </span>
                        </li>
                        <li className="flex items-center gap-4 border-y border-line py-4" data-testid="location-access">
                            <Clock size={16} className="shrink-0 text-ochre" />
                            <span className="text-sm text-mutedink">
                                New patients are usually seen within 7 days
                            </span>
                        </li>
                    </ul>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                >
                    <div className="mb-6 flex items-center justify-between">
                        <p
                            className="font-mono text-[11px] uppercase tracking-[0.3em] text-mutedink"
                            data-testid="hours-label"
                        >
                            Clinic hours
                        </p>
                        <span
                            data-testid="hours-status-badge"
                            className={`flex items-center gap-2 border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] ${
                                status.open
                                    ? "border-green-800/30 bg-green-50 text-green-900"
                                    : "border-ochre/40 bg-badge text-ink"
                            }`}
                        >
                            <span
                                className={`h-1.5 w-1.5 rounded-full animate-pulse-dot ${status.open ? "bg-green-700" : "bg-ochre"}`}
                            />
                            {status.label}
                        </span>
                    </div>
                    <div className="border border-line" data-testid="hours-table">
                        {HOURS_ROWS.map((row) => {
                            const isToday = row.dayNums.includes(today);
                            return (
                                <div
                                    key={row.id}
                                    data-testid={row.id}
                                    className={`flex items-center justify-between border-b border-line px-6 py-5 last:border-b-0 transition-colors duration-300 ${
                                        isToday ? "bg-ink text-bone" : "bg-bone hover:bg-white"
                                    }`}
                                >
                                    <span className="font-mono text-[11px] uppercase tracking-[0.2em]">
                                        {row.days}
                                        {isToday && <span className="ml-3 text-ochre">— today</span>}
                                    </span>
                                    <span
                                        className={`font-syne text-lg font-semibold tracking-tight ${
                                            row.time === "Closed" ? "text-mutedink" : ""
                                        }`}
                                    >
                                        {row.time}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-mutedink">
                        Walk-ins welcome when a clinician is free — calling ahead is best
                    </p>
                </motion.div>
            </div>
        </section>
    );
};
